/**
 * UserAvatar — Layer-1 shared avatar component.
 *
 * Renders a user's profile photo when available, or a colored circle with
 * the first letter of their name as fallback.
 *
 * When `editable` is true, clicking the avatar opens a file picker. On
 * successful upload the new photo appears instantly via AuthProvider re-render
 * (token rotation dispatches 'auth:token-refreshed').
 */
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Camera } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/shared/lib/utils";
import { useUploadPhoto } from "@/features/auth/hooks/useUploadPhoto";
import { toast } from "@/shared/lib/toast";
import { BrokenImageBadge } from "./BrokenImageBadge";

const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_BYTES = 5 * 1024 * 1024;

interface UserAvatarProps {
  /** Display name — used to derive the initials fallback. */
  name: string;
  /** Presigned S3 URL or null/undefined for initials fallback. */
  photoUrl?: string | null;
  /** Tailwind size class, e.g. "size-8" or "size-12". Defaults to "size-8". */
  sizeClass?: string;
  /** Tailwind text size for the fallback letter, e.g. "text-xs". Defaults to "text-xs". */
  textClass?: string;
  /** When true, renders an overlay and file-picker on click. */
  editable?: boolean;
  className?: string;
}

export function UserAvatar({
  name,
  photoUrl,
  sizeClass = "size-8",
  textClass = "text-xs",
  editable = false,
  className,
}: UserAvatarProps) {
  const { t } = useTranslation("common");
  const fileRef = useRef<HTMLInputElement>(null);
  const { mutate: upload, isPending } = useUploadPhoto();
  /* Radix Avatar already swaps to the initials fallback on load error; we only
     track it to surface the red "broken" marker. */
  const [photoError, setPhotoError] = useState(false);

  const initial = (name.trim()[0] ?? "?").toUpperCase();

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so selecting the same file again triggers onChange.
    e.target.value = "";

    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast.error(t("avatar.invalidType"));
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(t("avatar.fileTooLarge"));
      return;
    }

    upload(file, {
      onError: () => toast.error(t("avatar.uploadError")),
    });
  }

  const avatarEl = (
    <Avatar className={cn(sizeClass, className)}>
      <AvatarImage
        src={photoUrl ?? undefined}
        alt={t("avatar.photoAlt", { name })}
        onLoadingStatusChange={(status) => setPhotoError(status === "error")}
      />
      <AvatarFallback className={cn(textClass, "font-medium select-none")}>
        {initial}
      </AvatarFallback>
    </Avatar>
  );

  if (!editable) {
    return (
      <span className="relative inline-block">
        {avatarEl}
        {photoError && <BrokenImageBadge compact />}
      </span>
    );
  }

  return (
    <div className="relative inline-block group">
      <div
        role="button"
        tabIndex={0}
        aria-label={photoUrl ? t("avatar.changePhoto") : t("avatar.uploadPhoto")}
        className={cn("cursor-pointer rounded-full", isPending && "opacity-60")}
        onClick={() => !isPending && fileRef.current?.click()}
        onKeyDown={(e) => e.key === "Enter" && !isPending && fileRef.current?.click()}
      >
        {avatarEl}
        {/* Upload overlay shown on hover */}
        <span
          aria-hidden
          className={cn(
            "absolute inset-0 flex items-center justify-center",
            "rounded-full bg-black/40 opacity-0 group-hover:opacity-100",
            "transition-opacity",
            isPending && "opacity-100"
          )}
        >
          <Camera className="size-4 text-white" />
        </span>
        {photoError && <BrokenImageBadge compact />}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept={ACCEPTED_TYPES.join(",")}
        className="sr-only"
        onChange={handleFileChange}
      />
    </div>
  );
}
